import { selectedIsVibrationEnabled } from '@shared-core/features/settings'
import { AppLogger } from '@shared-core/services/logger'
import { useAppSelector } from '@shared-core/store'
import { wait } from '@shared-core/utils/wait'
import { useCallback } from 'react'
import { isIosDevice, triggerIosSwitchHaptic } from './ios-switch'
import { isTelegramHapticAvailable, triggerTelegramHaptic } from './telegram-haptic'

export type { HapticType } from './types'

import type { HapticType } from './types'

const logger = AppLogger.fork({ name: 'haptic' })

const HAPTIC_PATTERNS: Record<HapticType, number | number[]> = {
  selection: 10,
  impactLight: 15,
  impactMedium: 25,
  impactHeavy: 40,
  soft: 8,
  rigid: 20,
  notificationSuccess: [10, 50, 15],
  notificationWarning: [20, 80, 20],
  notificationError: [30, 50, 30, 50, 30],
}

function canNavigatorVibrate(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function'
}

function isAndroidDevice(): boolean {
  return typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent)
}

export function canVibrate(): boolean {
  if (isTelegramHapticAvailable()) {
    return true
  }

  if (isAndroidDevice() && canNavigatorVibrate()) {
    return true
  }

  return isIosDevice()
}

const HAPTIC_MIN_INTERVAL_MS = 80

const HAPTIC_PRIORITY: Record<HapticType, number> = {
  selection: 1,
  impactLight: 1,
  soft: 1,
  impactMedium: 2,
  rigid: 2,
  impactHeavy: 3,
  notificationSuccess: 4,
  notificationWarning: 4,
  notificationError: 5,
}

let pendingHapticType: HapticType | null = null
let isFlushScheduled = false
let lastHapticAt = 0

function requestAfterInteraction(callback: () => void): void {
  if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
    void wait(0).then(callback)
    return
  }

  window.requestAnimationFrame(() => {
    window.setTimeout(callback, 0)
  })
}

function getHigherPriorityType(nextType: HapticType, currentType: HapticType | null): HapticType {
  if (!currentType) {
    return nextType
  }

  return HAPTIC_PRIORITY[nextType] >= HAPTIC_PRIORITY[currentType] ? nextType : currentType
}

function fireWebHaptic(type: HapticType): void {
  try {
    // Telegram Mini App (iOS/Android/desktop): шлём через натив-bridge.
    // На Android impact/selection маппятся на notification — единственный тип,
    // что там реально вибрирует; navigator.vibrate в TG WebView зарезан
    // (vibrate() возвращает false, мотор не срабатывает).
    if (triggerTelegramHaptic(type)) {
      return
    }

    // iOS вне TMA: navigator.vibrate в WebKit отсутствует — switch-хак.
    if (isIosDevice()) {
      triggerIosSwitchHaptic()
      return
    }

    // Обычный браузер (напр. Chrome на Android): штатный Vibration API.
    if (canNavigatorVibrate()) {
      navigator.vibrate(HAPTIC_PATTERNS[type])
    }
  } catch (ignore) {
    logger.error('haptic failed', ignore)
  }
}

function flushWebHaptic(): void {
  const pendingType = pendingHapticType

  if (!pendingType) {
    isFlushScheduled = false
    return
  }

  const now = Date.now()
  const nextAllowedAt = lastHapticAt + HAPTIC_MIN_INTERVAL_MS

  if (now < nextAllowedAt) {
    setTimeout(flushWebHaptic, nextAllowedAt - now)
    return
  }

  pendingHapticType = null
  isFlushScheduled = false
  lastHapticAt = now
  fireWebHaptic(pendingType)
}

function triggerWebHaptic(type: HapticType): void {
  pendingHapticType = getHigherPriorityType(type, pendingHapticType)

  if (isFlushScheduled) {
    return
  }

  isFlushScheduled = true

  // Telegram postEvent синхронный: при быстрых тапах он может задерживать обработчик кнопки.
  requestAfterInteraction(flushWebHaptic)
}

export function useHaptic() {
  const isVibrationEnabled = useAppSelector(selectedIsVibrationEnabled)
  return useCallback(
    (type: HapticType) => {
      if (isVibrationEnabled) {
        triggerWebHaptic(type)
      }
    },
    [isVibrationEnabled],
  )
}
